/**
 * Fime – Desktop Navigation Controller
 *
 * Combines the mega menu and search bar in one module because they are
 * genuinely coupled: opening search closes the mega menu and vice versa,
 * and search directly reads mega menu state (menuOpen) and calls its
 * closeMenu() function. Splitting them would require exporting mutable
 * shared state across files for no real benefit.
 *
 * Mega menu animation:
 *  - First open OR re-open after leaving: drop-down fade (translateY + opacity).
 *  - Switching between items (menu stays open): directional slide from outside container.
 *  - Smooth timing: 0.35s with spring-like easing.
 *  - No horizontal scrollbar during animation.
 */

function reflow(el) {
	void el.getBoundingClientRect();
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. DESKTOP MEGA MENU
// ─────────────────────────────────────────────────────────────────────────────

const header = document.getElementById('site-header');
const desktopNavArea = document.getElementById('desktop-nav-area');
const megaContainer = document.getElementById('mega-menu-container');
const navItems = document.querySelectorAll('.nav-item[data-menu]');
const megaPanels = document.querySelectorAll('.mega-panel');

let activeMenuId = null;
let activeIndex = -1; // -1 means "no active item / treat next open as fresh"
let menuOpen = false;
let closeTimer = null;

function getPanel(menuId) {
	return document.getElementById('panel-' + menuId);
}

/**
 * Schedule a menu close and immediately reset activeIndex.
 * Resetting activeIndex here (not in closeMenu) ensures that if the user
 * re-enters a nav item within the debounce window, we still treat it as
 * a fresh open (drop-down) rather than a directional slide.
 */
function scheduleClose() {
	activeIndex = -1;
	closeTimer = setTimeout(closeMenu, 150);
}

/**
 * @param {string} menuId
 * @param {string} direction  'none' | 'right' | 'left'
 *
 * direction === 'none'  → first open / re-open: drop-down fade.
 * direction === 'right' → switching left→right: slide from right edge of container.
 * direction === 'left'  → switching right→left: slide from left edge of container.
 */
function openPanel(menuId, direction) {
	const incoming = getPanel(menuId);
	if (!incoming) return;

	megaContainer.classList.remove('hidden');

	// Instantly hide all other panels.
	megaPanels.forEach(function (panel) {
		if (panel === incoming) return;
		panel.style.transition = 'none';
		panel.style.transform = '';
		panel.style.opacity = '0';
		panel.classList.add('hidden');
	});

	if (direction === 'none') {
		// ── DROP-DOWN: smooth fade + subtle fall ────────────────────────────────
		incoming.classList.remove('hidden');
		incoming.style.transition = 'none';
		incoming.style.transform = 'translateY(-10px)';
		incoming.style.opacity = '0';

		reflow(incoming);

		incoming.style.transition = [
			'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
			'opacity 0.25s ease',
		].join(', ');
		incoming.style.transform = 'translateY(0)';
		incoming.style.opacity = '1';
	} else {
		// ── DIRECTIONAL SLIDE: content enters from outside container edge ───────
		// translateX(100%) / translateX(-100%) starts the panel fully outside.
		// mega-menu-container's overflow:hidden clips it — giving the illusion
		// the content arrives from off-screen.
		const startX = direction === 'right' ? '100%' : '-100%';

		incoming.classList.remove('hidden');
		incoming.style.transition = 'none';
		incoming.style.transform = 'translateX(' + startX + ')';
		incoming.style.opacity = '0';

		reflow(incoming);

		incoming.style.transition = [
			'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
			'opacity 0.35s ease',
		].join(', ');
		incoming.style.transform = 'translateX(0)';
		incoming.style.opacity = '1';
	}

	// Sync aria-expanded + chevron.
	navItems.forEach(function (item) {
		const trigger = item.querySelector('.nav-trigger');
		if (!trigger) return;
		const isActive = item.dataset.menu === menuId;
		trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
		const chevron = trigger.querySelector('.chevron-icon');
		if (chevron) chevron.style.transform = isActive ? 'rotate(180deg)' : '';
	});

	// Prevent horizontal scrollbar during slide animation.
	document.body.style.overflowX = 'hidden';

	activeMenuId = menuId;
	menuOpen = true;
}

function closeMenu() {
	megaContainer.classList.add('hidden');

	megaPanels.forEach(function (panel) {
		panel.classList.add('hidden');
		panel.style.transition = '';
		panel.style.transform = '';
		panel.style.opacity = '';
	});

	navItems.forEach(function (item) {
		const trigger = item.querySelector('.nav-trigger');
		if (!trigger) return;
		trigger.setAttribute('aria-expanded', 'false');
		const chevron = trigger.querySelector('.chevron-icon');
		if (chevron) chevron.style.transform = '';
	});

	document.body.style.overflowX = '';
	activeMenuId = null;
	activeIndex = -1;
	menuOpen = false;
}

// Wire each nav item.
navItems.forEach(function (item, index) {
	const trigger = item.querySelector('.nav-trigger');

	item.addEventListener('mouseenter', function () {
		clearTimeout(closeTimer);

		const direction =
			!menuOpen || activeIndex === -1
				? 'none'
				: index > activeIndex
					? 'right'
					: 'left';

		openPanel(item.dataset.menu, direction);
		activeIndex = index;
	});

	if (trigger) {
		trigger.addEventListener('click', function () {
			if (menuOpen && activeMenuId === item.dataset.menu) {
				closeMenu();
			} else {
				const direction =
					!menuOpen || activeIndex === -1
						? 'none'
						: index > activeIndex
							? 'right'
							: 'left';
				openPanel(item.dataset.menu, direction);
				activeIndex = index;
			}
		});
	}
});

if (desktopNavArea) {
	desktopNavArea.addEventListener('mouseleave', function () {
		scheduleClose();
	});
	desktopNavArea.addEventListener('mouseenter', function () {
		clearTimeout(closeTimer);
	});
}

if (megaContainer) {
	megaContainer.addEventListener('mouseenter', function () {
		clearTimeout(closeTimer);
	});
	megaContainer.addEventListener('mouseleave', function () {
		scheduleClose();
	});
}

document.addEventListener('keydown', function (e) {
	if (e.key === 'Escape' && menuOpen) closeMenu();
});

document.addEventListener('click', function (e) {
	if (menuOpen && header && !header.contains(e.target)) closeMenu();
});

// ─────────────────────────────────────────────────────────────────────────────
// 2. SEARCH BAR
// ─────────────────────────────────────────────────────────────────────────────

const searchToggle = document.getElementById('search-toggle');
const searchBar = document.getElementById('search-bar');
const searchInput = document.getElementById('search-input');
const searchClose = document.getElementById('search-close');

let searchOpen = false;

function openSearch() {
	if (menuOpen) closeMenu();

	if (!searchBar) return;

	searchBar.classList.remove('hidden');
	searchBar.style.transition = 'none';
	searchBar.style.transform = 'translateY(-10px)';
	searchBar.style.opacity = '0';

	reflow(searchBar);

	searchBar.style.transition = [
		'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
		'opacity 0.2s ease',
	].join(', ');
	searchBar.style.transform = 'translateY(0)';
	searchBar.style.opacity = '1';

	searchToggle && searchToggle.setAttribute('aria-expanded', 'true');

	setTimeout(function () {
		searchInput && searchInput.focus();
	}, 50);

	searchOpen = true;
}

function closeSearch() {
	if (!searchBar) return;

	searchBar.classList.add('hidden');
	searchBar.style.transition = '';
	searchBar.style.transform = '';
	searchBar.style.opacity = '';

	if (searchInput) searchInput.value = '';
	searchToggle && searchToggle.setAttribute('aria-expanded', 'false');

	searchOpen = false;
}

searchToggle &&
	searchToggle.addEventListener('click', function (e) {
		e.stopPropagation();
		searchOpen ? closeSearch() : openSearch();
	});

searchClose && searchClose.addEventListener('click', closeSearch);

searchInput &&
	searchInput.addEventListener('keydown', function (e) {
		if (e.key !== 'Enter') return;
		const query = searchInput.value.trim();
		if (!query) return;
		window.location.href = '/?s=' + encodeURIComponent(query);
	});

document.addEventListener('keydown', function (e) {
	if (e.key === 'Escape' && searchOpen) closeSearch();
});

document.addEventListener('click', function (e) {
	if (searchOpen && header && !header.contains(e.target)) closeSearch();
});

navItems.forEach(function (item) {
	item.addEventListener('mouseenter', function () {
		if (searchOpen) closeSearch();
	});
});
